import { useState, useEffect } from "react";
import type {
  EntityDetailModalState,
  InvestmentEntityGridRow,
  UseEntityDetailModalReturn,
} from "../types";

interface UseEntityDetailModalOptions {
  onOpen?: (entity: InvestmentEntityGridRow) => void;
  onClose?: () => void;
  clearDataOnClose?: boolean;
}

export const useEntityDetailModal = (
  options: UseEntityDetailModalOptions = {},
): UseEntityDetailModalReturn => {
  const { onOpen, onClose, clearDataOnClose = true } = options;

  const [modalState, setModalState] = useState<EntityDetailModalState>({
    isOpen: false,
    selectedEntity: null,
  });

  const openModal = (entity: InvestmentEntityGridRow) => {
    if (!entity || !entity.id) {
      return;
    }

    setModalState({
      isOpen: true,
      selectedEntity: entity,
    });

    if (onOpen) {
      onOpen(entity);
    }
  };

  const closeModal = () => {
    setModalState((prev) => ({
      ...prev,
      isOpen: false,
      selectedEntity: clearDataOnClose ? null : prev.selectedEntity,
    }));

    if (onClose) {
      onClose();
    }
  };

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && modalState.isOpen) {
        setModalState((prev) => ({
          ...prev,
          isOpen: false,
          selectedEntity: clearDataOnClose ? null : prev.selectedEntity,
        }));

        if (onClose) {
          onClose();
        }
      }
    };

    if (modalState.isOpen) {
      document.addEventListener("keydown", handleEscapeKey);
      return () => {
        document.removeEventListener("keydown", handleEscapeKey);
      };
    }
  }, [modalState.isOpen, clearDataOnClose, onClose]);

  return {
    modalState,
    openModal,
    closeModal,
  };
};
